import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { PageTransition } from './components/MotionWrappers';
import './assets/css/animations.css';
import ProductSearch from './components/ProductSearch';
import ProductDetail from './components/ProductDetail';
import Mission from './components/Mission';
import About from './components/About';
import Contact from './components/Contact';
import Brands from './components/Brands';
import Signin from './components/Signin';
import AuthCallback from './components/AuthCallback';
import Profile from './components/Profile';
import FashionBoard from './components/FashionBoard';
import BrandPage from './components/BrandPage';
import BrandPartnership from './components/BrandPartnership';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled';
import Discover from './components/Discover';
import Shop from './components/Shop';
import Footer from './components/Footer';
import NotFound from './components/NotFound';
import { CurrencyProvider } from './context/CurrencyContext';
import { AdminProvider } from './context/AdminContext';
import { BrandProvider } from './context/BrandContext';
import { ToastProvider } from './context/ToastContext';
// Admin Portal Components
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
// Brand Portal Components
import BrandLogin from './components/BrandLogin';
import BrandProfile from './components/BrandProfile';
import BrandProductForm from './components/BrandProductForm';
import BrandSetupPassword from './components/BrandSetupPassword';
import BrandForgotPassword from './components/BrandForgotPassword';
import BrandResetPassword from './components/BrandResetPassword';
// New Brand Portal Dashboard Components
import { 
  DashboardLayout, 
  BrandOverview, 
  BrandCollection, 
  BrandAnalytics, 
  BrandInsights,
  BrandDocumentation
} from './components/brand-portal';
import BrandProductDetail from './components/brand-portal/BrandProductDetail';
import PageViewTracker from './components/PageViewTracker';
import CurrencySelector from './components/CurrencySelector';
import NotifyPopup from './components/NotifyPopup';

// Wrapper component to conditionally render footer
function AppContent() {
  const location = useLocation();
  
  // Don't show main footer on brand portal dashboard pages (they have their own footer)
  // But show it on login/setup pages
  const isBrandPortalDashboard = location.pathname.startsWith('/brand-portal') && 
    !location.pathname.includes('/login') && 
    !location.pathname.includes('/setup') &&
    !location.pathname.includes('/forgot-password') &&
    !location.pathname.includes('/reset-password');
  
  // Don't show footer on admin pages either
  const isAdminPage = location.pathname.startsWith('/app-admin');
  
  const showFooter = !isBrandPortalDashboard && !isAdminPage;

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <PageViewTracker />
      <CurrencySelector />
      {!isBrandPortalDashboard && !isAdminPage && <NotifyPopup />}
      <AnimatePresence mode="wait">
        <PageTransition key={location.pathname}>
          <Routes location={location}>
            {/* Public Routes */}
            <Route path="/" element={<ProductSearch />} />
            <Route path="/product/:productId" element={<ProductDetail />} />
            <Route path="/vision" element={<Mission />} />
            <Route path="/blog" element={<About />} />
            <Route path="/about" element={<About />} />
            <Route path="/brands" element={<Brands />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/signin" element={<Signin />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/fashion-board" element={<FashionBoard />} />
            <Route path="/brand/:brandName" element={<BrandPage />} />
            <Route path="/partner" element={<BrandPartnership />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/shop" element={<Shop />} />
                    
            {/* Admin Portal Routes (under /app-admin to avoid conflict with Django /admin/) */}
            <Route path="/app-admin/login" element={<AdminLogin />} />
            <Route path="/app-admin/dashboard" element={<AdminDashboard />} />
            
            {/* Brand Portal Auth Routes */}
            <Route path="/brand-portal/login" element={<BrandLogin />} />
            <Route path="/brand-portal/setup/:token" element={<BrandSetupPassword />} />
            <Route path="/brand-portal/setup-password" element={<BrandSetupPassword />} />
            <Route path="/brand-portal/forgot-password" element={<BrandForgotPassword />} />
            <Route path="/brand-portal/reset-password" element={<BrandResetPassword />} />
            
            {/* Brand Portal Dashboard with Layout */}
            <Route path="/brand-portal" element={<DashboardLayout />}>
              <Route path="dashboard" element={<BrandOverview />} />
              <Route path="collection" element={<BrandCollection />} />
              <Route path="products/:productId" element={<BrandProductDetail />} />
              <Route path="analytics" element={<BrandAnalytics />} />
              <Route path="insights" element={<BrandInsights />} />
              <Route path="profile" element={<BrandProfile />} />
              <Route path="docs" element={<BrandDocumentation />} />
              <Route path="products" element={<Navigate to="/brand-portal/collection" replace />} />
              <Route path="products/new" element={<BrandProductForm />} />
              <Route path="products/:productId/edit" element={<BrandProductForm />} />
            </Route>
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </AnimatePresence>
      {showFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ToastProvider>
        <CurrencyProvider>
          <AdminProvider>
            <BrandProvider>
              <Router>
                <AppContent />
              </Router>
            </BrandProvider>
          </AdminProvider>
        </CurrencyProvider>
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
